import { createStore } from '../../core/state/store';
import en from './locales/en.json';
import es from './locales/es.json';
import authEn from './locales/areas/auth.en.json';
import authEs from './locales/areas/auth.es.json';
import shellEn from './locales/areas/shell.en.json';
import shellEs from './locales/areas/shell.es.json';
import worldmapEn from './locales/areas/worldmap.en.json';
import worldmapEs from './locales/areas/worldmap.es.json';
import colonyEn from './locales/areas/colony.en.json';
import colonyEs from './locales/areas/colony.es.json';
import battleEn from './locales/areas/battle.en.json';
import battleEs from './locales/areas/battle.es.json';
import shopEn from './locales/areas/shop.en.json';
import shopEs from './locales/areas/shop.es.json';
import socialEn from './locales/areas/social.en.json';
import socialEs from './locales/areas/social.es.json';
import termsEn from './locales/areas/terms.en.json';
import termsEs from './locales/areas/terms.es.json';

/**
 * UI translation for the game and the landing page.
 *
 * Deliberately a ~70-line module rather than a dependency: the whole job is a
 * flat key -> string lookup with interpolation and a fallback, and every
 * off-the-shelf i18n library ships a plural/context/namespace engine this game
 * has no use for yet.
 *
 * The locale files are plain JSON, imported statically so Vite bundles and
 * type-checks them — a missing key is a build error in `en.json`'s shape, not
 * a runtime blank. English is the source of truth: `es.json` is typed against
 * it (see `Translations`), so adding an English key without a Spanish one is a
 * compile error rather than a string that silently ships in the wrong
 * language.
 *
 * SCOPE NOTE — read before assuming a string is translatable.
 * `frontend/src/core/**` is template-owned and must not be edited in a game
 * repo, so the six auth screens (login, register, verify, forgot, reset,
 * check-email) and the payment widgets cannot take `t()` calls at all. They
 * stay English for now. The legal seam for them is `engine/auth-wrapper.ts`,
 * which already post-processes core-rendered auth DOM for layout fixes and
 * could do a text pass the same way — that is a separate piece of work, not
 * something this module silently does.
 */

export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * Strings are split by UI area (`locales/areas/<area>.<locale>.json`) on top
 * of the shared base table (`locales/<locale>.json`: nav, common, shell
 * basics, settings, landing). Splitting keeps each screen's copy next to its
 * siblings and lets areas be translated independently without every change
 * landing in one giant file. Keys are namespaced by area (`worldmap.*`,
 * `colony.*`, …) and must be unique across ALL files — a duplicate would
 * silently overwrite; i18n.test.ts fails on one. `terms.*` holds the shared
 * game glossary (unit/building/resource names) every area reuses.
 */
export const LOCALE_SOURCES = {
  en: [en, termsEn, authEn, shellEn, worldmapEn, colonyEn, battleEn, shopEn, socialEn],
  es: [es, termsEs, authEs, shellEs, worldmapEs, colonyEs, battleEs, shopEs, socialEs],
} as const;

const EN_TABLE = Object.assign({}, ...LOCALE_SOURCES.en) as typeof en & typeof termsEn &
  typeof authEn & typeof shellEn & typeof worldmapEn & typeof colonyEn & typeof battleEn & typeof shopEn & typeof socialEn;

/** English is the source of truth; every other locale must supply the same keys. */
export type Translations = Record<keyof typeof EN_TABLE, string>;

export const TABLES: Record<Locale, Translations> = {
  en: EN_TABLE as Translations,
  es: Object.assign({}, ...LOCALE_SOURCES.es) as Translations,
};

const STORAGE_KEY = 'dunes:locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Saved choice, else the browser's preference, else English. Only the language
 * subtag is considered — `es-419` and `es-MX` are both Spanish for our purposes.
 */
function initialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private browsing can throw on read. Fall through to the browser hint.
  }
  const navLang = typeof navigator !== 'undefined' ? navigator.language : '';
  const primary = (navLang || '').split('-')[0].toLowerCase();
  return isLocale(primary) ? primary : 'en';
}

export const localeStore = createStore<Locale>(initialLocale());

// `<html lang>` was only stamped by setLocale, i.e. never after the reload a
// language switch triggers — so a Spanish session still announced itself as
// English to screen readers and the browser's translate prompt.
if (typeof document !== 'undefined') document.documentElement.lang = localeStore.get();

export function getLocale(): Locale {
  return localeStore.get();
}

export function setLocale(locale: Locale): void {
  if (!isLocale(locale) || locale === localeStore.get()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not fatal — the choice just will not survive a reload.
  }
  // `lang` drives screen readers, browser translation prompts and CSS
  // `:lang()`. Keeping it in step with the store is cheap and easy to forget.
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  localeStore.set(locale);
}

/**
 * Translate `key`, interpolating `{name}` placeholders from `vars`.
 *
 * Falls back to English, then to the key itself. Returning the key rather than
 * an empty string makes a missing translation obvious in the UI instead of
 * silently collapsing the layout.
 */
export function t(key: keyof Translations, vars?: Record<string, string | number>): string {
  const table = TABLES[localeStore.get()] ?? TABLES.en;
  const raw = table[key] ?? TABLES.en[key] ?? (key as string);
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

/**
 * Translate a key built at runtime from backend data (an enum value, a
 * palette id), falling back to `fallback` when the table has no such key —
 * so an enum the frontend doesn't know yet shows its raw value, not a
 * dotted key.
 */
export function tOr(key: string, fallback: string): string {
  return Object.prototype.hasOwnProperty.call(TABLES.en, key) ? t(key as keyof Translations) : fallback;
}

/** Test seam — the store is module-level and would leak between cases. */
export function __resetLocaleForTests(): void {
  localeStore.set('en');
}
